const BASE_CLASS = 'scroll-reveal-item';
const VISIBLE_CLASS = 'scroll-reveal-visible';
const GROUP_ATTRIBUTE = 'data-scroll-group';
const TARGET_ATTRIBUTE = 'data-scroll-reveal';
const IGNORE_ATTRIBUTE = 'data-scroll-ignore';
const MAX_STAGGER_DELAY = 28;
const MAX_STAGGER_TOTAL = 168;
const revealedTargets = new WeakSet();

const defaultOptions = {
  threshold: 0,
  rootMargin: '0px 0px 8% 0px',
  once: true
};

/**
 * Collect targets defined with data-scroll-group (children) and data-scroll-reveal (element itself)
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
function collectTargets(root) {
  const targets = [];
  const seen = new Set();

  const manualTargets = root.querySelectorAll(`[${TARGET_ATTRIBUTE}]`);
  manualTargets.forEach((el) => {
    if (seen.has(el)) {
      return;
    }
    seen.add(el);
    targets.push(el);
  });

  const groupContainers = root.querySelectorAll(`[${GROUP_ATTRIBUTE}]`);
  groupContainers.forEach((container) => {
    const requestedStep = Number(container.dataset.scrollStep || MAX_STAGGER_DELAY);
    const step = Math.min(requestedStep, MAX_STAGGER_DELAY);
    const includeSelf = container.dataset.scrollGroup === 'self' || container.classList.contains('scroll-container');
    const elements = [];

    if (includeSelf) {
      elements.push(container);
    }

    Array.from(container.children || []).forEach((child) => {
      if (
        !child ||
        child.nodeType !== 1 ||
        child.hasAttribute(IGNORE_ATTRIBUTE) ||
        child.tagName === 'SCRIPT' ||
        child.tagName === 'STYLE' ||
        child.tagName === 'BR'
      ) {
        return;
      }
      elements.push(child);
    });

    elements.forEach((el, index) => {
      if (seen.has(el)) {
        return;
      }
      seen.add(el);
      el.style.setProperty('--scroll-delay', `${Math.min(index * step, MAX_STAGGER_TOTAL)}ms`);
      targets.push(el);
    });
  });

  const scrollGalleries = root.querySelectorAll('.scroll-container');
  scrollGalleries.forEach((el) => {
    if (seen.has(el)) {
      return;
    }
    seen.add(el);
    targets.push(el);
  });

  return targets;
}

function isElementVisible(el, threshold = 0) {
  if (!el || typeof window === 'undefined') {
    return false;
  }
  const rect = el.getBoundingClientRect();
  const viewHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewWidth = window.innerWidth || document.documentElement.clientWidth || 0;

  if (viewHeight === 0 || viewWidth === 0) {
    return false;
  }

  const elementHeight = rect.height || 1;
  const visibleHeight = Math.max(0, Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0));
  if (visibleHeight === 0) {
    return false;
  }
  if (threshold <= 0) {
    return true;
  }
  const ratio = visibleHeight / elementHeight;
  return ratio >= threshold;
}

export function initScrollAnimations(root, options = {}) {
  if (!root) {
    return () => {};
  }

  const settings = {
    ...defaultOptions,
    ...options
  };

  const targets = collectTargets(root);

  if (!targets.length) {
    return () => {};
  }

  const supportsObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window;

  // On slow phones the browser can paint the freshly mounted page before this
  // nextTick runs. If we then add the hidden state while the reveal transition
  // is live, the element visibly animates from its final position down and out
  // (opacity 1 -> 0, translateY 0 -> 10px) before the observer plays the real
  // entrance. Freeze transitions while the hidden state lands, then restore
  // them so the actual reveal still animates.
  const inlineTransition = new Map();
  targets.forEach((target) => {
    inlineTransition.set(target, target.style.transition || '');
    target.style.transition = 'none';
  });
  const freshTargets = [];
  targets.forEach((target) => {
    target.classList.add(BASE_CLASS);
    if (revealedTargets.has(target)) {
      target.classList.add(VISIBLE_CLASS);
    } else {
      freshTargets.push(target);
    }
  });
  // Force the hidden state to settle in one step before re-enabling the
  // transition that the reveal animation depends on.
  if (typeof document !== 'undefined' && targets.length) {
    void document.documentElement.offsetHeight;
  }
  targets.forEach((target) => {
    target.style.transition = inlineTransition.get(target);
  });

  if (!supportsObserver) {
    freshTargets.forEach((target) => {
      target.classList.add(VISIBLE_CLASS);
      revealedTargets.add(target);
    });
    return () => {
      targets.forEach((target) => {
        target.classList.remove(BASE_CLASS, VISIBLE_CLASS);
        target.style.removeProperty('--scroll-delay');
      });
    };
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(VISIBLE_CLASS);
        revealedTargets.add(entry.target);
        if (settings.once) {
          observer.unobserve(entry.target);
        }
      } else if (!settings.once) {
        entry.target.classList.remove(VISIBLE_CLASS);
      }
    });
  }, settings);

  const hasWindow = typeof window !== 'undefined';
  let rafId = null;
  let timeoutId = null;
  let loadHandler = null;

  const revealVisibleTargets = () => {
    targets.forEach((target) => {
      if (target.classList.contains(VISIBLE_CLASS)) {
        return;
      }
      if (isElementVisible(target, settings.threshold)) {
        target.classList.add(VISIBLE_CLASS);
        revealedTargets.add(target);
        if (settings.once) {
          observer.unobserve(target);
        }
      }
    });
  };

  if (hasWindow) {
    const raf = window.requestAnimationFrame || ((cb) => window.setTimeout(cb, 16));
    rafId = raf(() => {
      // Let the initial hidden state reach one frame before revealing anything
      // already on screen. Existing targets are remembered across a refresh,
      // so filtering never makes settled content flash or replay.
      freshTargets.forEach((target) => observer.observe(target));
      rafId = raf(() => {
        revealVisibleTargets();
        rafId = null;
      });
    });
    timeoutId = window.setTimeout(() => {
      revealVisibleTargets();
    }, 400);
    loadHandler = () => {
      revealVisibleTargets();
    };
    window.addEventListener('load', loadHandler, { once: true });
  } else {
    freshTargets.forEach((target) => observer.observe(target));
  }

  return () => {
    observer.disconnect();
    if (hasWindow) {
      if (rafId !== null) {
        if (window.cancelAnimationFrame) {
          window.cancelAnimationFrame(rafId);
        } else {
          window.clearTimeout(rafId);
        }
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      if (loadHandler) {
        window.removeEventListener('load', loadHandler);
      }
    }
    targets.forEach((target) => {
      target.classList.remove(BASE_CLASS, VISIBLE_CLASS);
      target.style.removeProperty('--scroll-delay');
    });
  };
}
